import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTablePagination from '@material-ui/core/TablePagination';
import MuiPaper from '@material-ui/core/Paper';
import MuiBox from '@material-ui/core/Box';

import TableSelectColumn from '../TableSelectColumn';
import SelectionActions from '../SelectionActions';
import useTableSelection from '../../hooks/useTableSelection';

const styles = theme => ({
  root: {
    width: '100%',
    position: 'relative',
  },
  tableContainer: {
    maxHeight: '100%',
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[50],
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  noData: {
    padding: theme.spacing(3),
    textAlign: 'center',
  },
  paginationBox: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

/**
 * Reusable table component with selection functionality
 * Supports row selection, batch actions, and pagination
 */
const SelectableTable = props => {
  const {
    classes,
    columns,
    data,
    idField,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
    onExportSelected,
    onApproveSelected,
    loading,
    noDataMessage,
  } = props;

  // Use the selection hook to manage selected rows
  const {
    selectedIds,
    selectedItems,
    isAllSelected,
    isSomeSelected,
    isSelected,
    toggleSelection,
    toggleSelectAll,
    clearSelection,
  } = useTableSelection(data, idField);

  // Handle row click to toggle selection
  const handleRowClick = (event, id) => {
    toggleSelection(id);
  };

  return (
    <MuiPaper className={classes.root}>
      <MuiTableContainer className={classes.tableContainer}>
        <MuiTable className={classes.table} aria-label="selectable table">
          <MuiTableHead className={classes.tableHead}>
            <MuiTableRow>
              <TableSelectColumn
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={toggleSelectAll}
                tooltip={isAllSelected ? 'Deselect all' : 'Select all'}
              />
              {columns.map(column => (
                <MuiTableCell
                  key={column.id}
                  align={column.align || 'left'}
                  style={{ minWidth: column.minWidth }}>
                  {column.label}
                </MuiTableCell>
              ))}
            </MuiTableRow>
          </MuiTableHead>
          <MuiTableBody>
            {loading ? (
              <MuiTableRow>
                <MuiTableCell colSpan={columns.length + 1} align="center">
                  Loading...
                </MuiTableCell>
              </MuiTableRow>
            ) : data.length === 0 ? (
              <MuiTableRow>
                <MuiTableCell colSpan={columns.length + 1} align="center">
                  {noDataMessage || 'No data available'}
                </MuiTableCell>
              </MuiTableRow>
            ) : (
              data.map(row => {
                const isItemSelected = isSelected(row[idField]);

                return (
                  <MuiTableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row[idField]}
                    selected={isItemSelected}
                    className={classes.tableRow}>
                    <TableSelectColumn
                      checked={isItemSelected}
                      onChange={event => handleRowClick(event, row[idField])}
                    />
                    {columns.map(column => {
                      const value = row[column.id];
                      return (
                        <MuiTableCell key={column.id} align={column.align}>
                          {column.format ? column.format(value, row) : value}
                        </MuiTableCell>
                      );
                    })}
                  </MuiTableRow>
                );
              })
            )}
          </MuiTableBody>
        </MuiTable>
      </MuiTableContainer>

      <MuiBox className={classes.paginationBox}>
        <MuiTablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </MuiBox>

      <SelectionActions
        selectedCount={selectedIds.length}
        onClearSelection={clearSelection}
        onExportSelected={() => onExportSelected(selectedItems)}
        onApproveSelected={() => onApproveSelected(selectedItems)}
        disableExport={selectedIds.length === 0}
        disableApprove={selectedIds.length === 0}
      />
    </MuiPaper>
  );
};

SelectableTable.propTypes = {
  classes: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      minWidth: PropTypes.number,
      align: PropTypes.oneOf(['left', 'right', 'center']),
      format: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.array.isRequired,
  idField: PropTypes.string,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onExportSelected: PropTypes.func,
  onApproveSelected: PropTypes.func,
  loading: PropTypes.bool,
  noDataMessage: PropTypes.string,
};

SelectableTable.defaultProps = {
  idField: 'id',
  loading: false,
  onExportSelected: () => {},
  onApproveSelected: () => {},
};

export default withStyles(styles)(SelectableTable);
